import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  //  Grid, // REMOVIDO: Eliminando o componente Grid
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { API_URLS } from "../config/apiUrls";

// Definir os tipos das props (inalterado)
interface ConfiguracaoProps {
  token: string; // Token recebido como prop
}

interface LegalContent {
  id: string;
  type: string;
  content: string;
  version: string;
  createdAt: string;
  updatedAt: string;
}

const Configuracao: React.FC<ConfiguracaoProps> = ({ token }) => {
  const [termos, setTermos] = useState<string>(
    "Texto padrão dos Termos de Uso."
  );
  const [politicaPrivacidade, setPoliticaPrivacidade] = useState<string>(
    "Texto padrão da Política de Privacidade."
  );
  const [mensagem, setMensagem] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  // Variáveis para armazenar as versões atuais
  const [termosVersion, setTermosVersion] = useState<string>("1.0.0");
  const [politicaVersion, setPoliticaVersion] = useState<string>("1.0.0");

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  // Função para incrementar a versão (inalterada)
  const incrementVersion = (version: string): string => {
    const versionParts = version.split(".").map(Number);
    versionParts[2]++;
    if (versionParts[2] > 9) {
      versionParts[2] = 0;
      versionParts[1]++;
      if (versionParts[1] > 9) {
        versionParts[1] = 0;
        versionParts[0]++;
      }
    }
    return versionParts.join(".");
  };

  // Função para obter a maior versão de um conteúdo (inalterada)
  const getLatestVersion = (
    contents: LegalContent[],
    type: string
  ): LegalContent | null => {
    const filteredContents = contents.filter((item) => item.type === type);
    if (filteredContents.length === 0) return null;

    const sortedContents = filteredContents.sort((a, b) => {
      const versionA = a.version.split(".").map(Number);
      const versionB = b.version.split(".").map(Number);
      for (let i = 0; i < versionA.length; i++) {
        if (versionA[i] > versionB[i]) return -1;
        if (versionA[i] < versionB[i]) return 1;
      }
      return 0;
    });

    return sortedContents[0];
  };

  // Função para realizar a requisição GET e carregar os dados (inalterada)
  useEffect(() => {
    const fetchLegalContent = async () => {
      try {
        const response = await fetch(API_URLS.legal, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Erro ao carregar os dados.");
        }

        const data: LegalContent[] = await response.json();

        const termosData = getLatestVersion(data, "TERMS_OF_USE");
        const politicaData = getLatestVersion(data, "PRIVACY_POLICY");

        if (termosData) {
          setTermos(termosData.content);
          setTermosVersion(termosData.version);
        }
        if (politicaData) {
          setPoliticaPrivacidade(politicaData.content);
          setPoliticaVersion(politicaData.version);
        }
      } catch (err) {
        setError("Erro ao carregar os dados. Tente novamente.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchLegalContent();
  }, [token]);

  // Função para atualizar os Termos de Uso (inalterada)
  const atualizarTermos = async () => {
    try {
      const newVersion = incrementVersion(termosVersion);
      const response = await fetch(API_URLS.legal, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "TERMS_OF_USE",
          content: termos,
          version: newVersion,
        }),
      });

      if (!response.ok) {
        throw new Error("Erro ao atualizar os Termos de Uso.");
      }

      setTermosVersion(newVersion);
      showSnackbar("Termos de Uso atualizados com sucesso!", "success");
    } catch (error) {
      showSnackbar("Erro ao atualizar os Termos de Uso.", "error");
    }
  };

  // Função para atualizar a Política de Privacidade (inalterada)
  const atualizarPoliticaPrivacidade = async () => {
    try {
      const newVersion = incrementVersion(politicaVersion);
      const response = await fetch(API_URLS.legal, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "PRIVACY_POLICY",
          content: politicaPrivacidade,
          version: newVersion,
        }),
      });

      if (!response.ok) {
        throw new Error("Erro ao atualizar a Política de Privacidade.");
      }

      setPoliticaVersion(newVersion);
      showSnackbar(
        "Política de Privacidade atualizada com sucesso!",
        "success"
      );
    } catch (error) {
      showSnackbar("Erro ao atualizar a Política de Privacidade.", "error");
    }
  };

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography gutterBottom variant="h2">
        Configurações
      </Typography>

      {/* Termos de Uso - SUBSTITUINDO GRID */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6">
          Termos de Uso (Versão Atual: **{termosVersion}**)
        </Typography>
        <TextField
          value={termos}
          onChange={(e) => setTermos(e.target.value)}
          multiline
          rows={5}
          fullWidth
          variant="outlined"
          margin="normal"
          InputProps={{
            sx: {
              "& .MuiInputBase-inputMultiline": {
                resize: "vertical",
              },
            },
          }}
        />
        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={atualizarTermos}
            disabled={termos.trim() === ""}
          >
            Atualizar Termos de Uso
          </Button>
        </Box>
      </Box>

      {/* Política de Privacidade - SUBSTITUINDO GRID */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6">
          Política de Privacidade (Versão Atual: **{politicaVersion}**)
        </Typography>
        <TextField
          value={politicaPrivacidade}
          onChange={(e) => setPoliticaPrivacidade(e.target.value)}
          multiline
          rows={5}
          fullWidth
          variant="outlined"
          margin="normal"
          InputProps={{
            sx: {
              "& .MuiInputBase-inputMultiline": {
                resize: "vertical",
              },
            },
          }}
        />
        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={atualizarPoliticaPrivacidade}
            disabled={politicaPrivacidade.trim() === ""}
          >
            Atualizar Política de Privacidade
          </Button>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Configuracao;
